import posthog from 'posthog-js'

type LinkType = 'website' | 'linkedin' | 'youtube' | 'email' | 'phone'

const OWN_HOST = /(^|\.)electrification-academy\.(com|org)$/i

function classify(url: URL): LinkType | null {
  if (url.protocol === 'mailto:') return 'email'
  if (url.protocol === 'tel:') return 'phone'
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
  if (url.hostname === window.location.hostname || OWN_HOST.test(url.hostname)) return null
  // Media and document files served from our own blob store are downloads, tracked elsewhere.
  if (/\.blob\.vercel-storage\.com$/i.test(url.hostname)) return null
  if (/(^|\.)(linkedin\.com|lnkd\.in)$/i.test(url.hostname)) return 'linkedin'
  if (/(^|\.)(youtube\.com|youtu\.be)$/i.test(url.hostname)) return 'youtube'
  return 'website'
}

// One document-level listener records every click that leaves the site
// (vendor websites, product pages on the vendor's domain, LinkedIn, YouTube,
// email). Vendor reports filter these by page_path. A delegated listener means
// rich-text links and CMS-driven cards are covered without touching each one.
export function startOutboundLinkTracking(): () => void {
  const onClick = (event: MouseEvent) => {
    if (!posthog.__loaded || event.defaultPrevented) return
    // left click and middle click (open in new tab) both count
    if (event.type === 'click' && event.button !== 0) return
    if (event.type === 'auxclick' && event.button !== 1) return
    const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null
    if (!anchor) return
    let url: URL
    try {
      url = new URL(anchor.href, window.location.href)
    } catch {
      return
    }
    const linkType = classify(url)
    if (!linkType) return
    // email and phone links carry the address itself: record only that one was clicked
    const isContact = linkType === 'email' || linkType === 'phone'
    const text = isContact
      ? ''
      : (anchor.textContent || anchor.getAttribute('aria-label') || '').trim().slice(0, 120)
    posthog.capture(
      'outbound_link_clicked',
      {
        link_type: linkType,
        target_url: isContact ? url.protocol : url.href,
        target_domain: isContact ? null : url.hostname.replace(/^www\./i, ''),
        link_text: text || null,
        page_path: window.location.pathname,
        // EA's own LinkedIn/YouTube (footer "Follow us") are not vendor clicks
        ea_channel: !isContact && /electrification-?academy/i.test(url.href),
        in_footer: Boolean(anchor.closest('footer')),
      },
      // same-tab navigations unload the page; a beacon survives that
      { transport: 'sendBeacon' },
    )
  }
  document.addEventListener('click', onClick, true)
  document.addEventListener('auxclick', onClick, true)
  return () => {
    document.removeEventListener('click', onClick, true)
    document.removeEventListener('auxclick', onClick, true)
  }
}
